'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Picked but not-yet-sent attachment. Lives entirely in the composer
 * — the upload pipeline kicks in only when the user actually clicks
 * send (see `useSendChannelMessage`). Each entry carries a local
 * preview URL produced via `URL.createObjectURL(file)` so the
 * thumbnail strip can render before any network round trip.
 */
export interface ComposerAttachment {
  /** Local id — stable across re-renders, used for the X-remove key. */
  id: string;
  file: File;
  filename: string;
  contentType: string;
  /** `URL.createObjectURL(file)` — revoked when the entry is removed. */
  localPreviewUrl: string;
}

const kImageContentTypes = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
]);
const kVideoContentTypes = new Set(['video/mp4', 'video/quicktime']);

const kMaxImageBytes = 10 * 1024 * 1024;
const kMaxVideoBytes = 4 * 1024 * 1024 * 1024;

export type ComposerAttachmentRejection =
  | { kind: 'unsupported'; filename: string }
  | { kind: 'too-large'; filename: string; limitBytes: number };

interface UseComposerAttachmentsResult {
  attachments: ComposerAttachment[];
  files: File[];
  add: (files: FileList | File[]) => ComposerAttachmentRejection[];
  remove: (id: string) => void;
  clear: () => void;
  /**
   * Drop the staged list without revoking object URLs — used by the
   * optimistic-send path, which keeps the same URLs alive on the
   * provider's medias state until the optimistic row resolves or is
   * removed.
   */
  detach: () => void;
  hasAttachments: boolean;
}

/**
 * Local attachment state for the channel composer — pick / remove /
 * clear. Returns a stable handle plus a derived `File[]` the send
 * action can hand straight to `useSendChannelMessage`.
 *
 * Object URLs are revoked on remove and on unmount. Validation
 * happens at pick-time: unsupported content-types and oversize files
 * are rejected before they make it into the list so the strip only
 * ever holds candidates the server will accept.
 */
export function useComposerAttachments(): UseComposerAttachmentsResult {
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  // Revoke any remaining object URLs when the composer unmounts —
  // captures the latest list via a ref so the cleanup isn't tied to
  // the very first attachments value.
  useEffect(() => {
    return () => {
      for (const item of attachmentsRef.current) {
        URL.revokeObjectURL(item.localPreviewUrl);
      }
    };
  }, []);

  const add = useCallback(
    (input: FileList | File[]): ComposerAttachmentRejection[] => {
      const incoming = Array.from(input);
      const rejections: ComposerAttachmentRejection[] = [];
      const accepted: ComposerAttachment[] = [];
      for (const file of incoming) {
        const reason = validateFile(file);
        if (reason) {
          rejections.push(reason);
          continue;
        }
        accepted.push({
          id: makeId(),
          file,
          filename: file.name,
          contentType: file.type,
          localPreviewUrl: URL.createObjectURL(file),
        });
      }
      if (accepted.length) {
        setAttachments(prev => [...prev, ...accepted]);
      }
      return rejections;
    },
    [],
  );

  const remove = useCallback((id: string) => {
    setAttachments(prev => {
      const next = prev.filter(item => {
        if (item.id !== id) return true;
        URL.revokeObjectURL(item.localPreviewUrl);
        return false;
      });
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setAttachments(prev => {
      for (const item of prev) URL.revokeObjectURL(item.localPreviewUrl);
      return [];
    });
  }, []);

  // Hand the attachments off without revoking object URLs — the
  // optimistic-send pipeline keeps the same URLs alive on its
  // pending-medias state so the bubble's preview tiles render from
  // them. The provider revokes when the optimistic row exits.
  const detach = useCallback(() => {
    setAttachments([]);
  }, []);

  return {
    attachments,
    files: attachments.map(a => a.file),
    add,
    remove,
    clear,
    detach,
    hasAttachments: attachments.length > 0,
  };
}

function validateFile(file: File): ComposerAttachmentRejection | undefined {
  const type = file.type;
  const isImage = kImageContentTypes.has(type);
  const isVideo = kVideoContentTypes.has(type);
  if (!isImage && !isVideo) {
    return { kind: 'unsupported', filename: file.name };
  }
  const limit = isImage ? kMaxImageBytes : kMaxVideoBytes;
  if (file.size > limit) {
    return { kind: 'too-large', filename: file.name, limitBytes: limit };
  }
  return undefined;
}

function makeId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `a_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
